import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function RolesManager({ usuario }) {
  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [selectedRol, setSelectedRol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', descripcion: '' });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('authToken');

  const fetchWithAuth = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, modulosRes] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/api/roles`),
        fetchWithAuth(`${API_BASE_URL}/api/modulos`)
      ]);
      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (modulosRes.ok) setModulos(await modulosRes.json());
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermisos = async (rolId) => {
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/api/permisos/${rolId}`);
      if (res.ok) setPermisos(await res.json());
    } catch (err) {
      console.error('Error cargando permisos', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedRol) {
      fetchPermisos(selectedRol.id);
    } else {
      setPermisos([]);
    }
  }, [selectedRol]);

  const handleSelectRol = (rol) => {
    setSelectedRol(rol);
    setError('');
  };

  const handleTogglePermiso = async (permisoId, campo, valor) => {
    setSaving(true);
    try {
      const permiso = permisos.find(p => p.id === permisoId);
      if (!permiso) return;
      const updated = { ...permiso, [campo]: valor };
      const res = await fetchWithAuth(`${API_BASE_URL}/api/permisos/${permisoId}`, {
        method: 'PUT',
        body: JSON.stringify({
          puede_ver: updated.puede_ver,
          puede_crear: updated.puede_crear,
          puede_editar: updated.puede_editar,
          puede_eliminar: updated.puede_eliminar
        })
      });
      if (res.ok) {
        setPermisos(prev => prev.map(p => p.id === permisoId ? { ...p, [campo]: valor } : p));
      } else {
        setError('Error al actualizar permiso');
      }
    } catch (err) {
      setError('Error de conexion');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRol = async (e) => {
    e.preventDefault();
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/api/roles`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({ nombre: '', descripcion: '' });
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Error al crear rol');
      }
    } catch (err) {
      setError('Error de conexion');
    }
  };

  const handleDeleteRol = async (id) => {
    if (!window.confirm('¿Estas seguro de eliminar este rol?')) return;
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/api/roles/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setSelectedRol(null);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Error al eliminar rol');
      }
    } catch (err) {
      setError('Error de conexion');
    }
  };

  return (
    <div className="manager-container">
      <div className="header-actions">
        <h2>Roles y Permisos</h2>
        <button onClick={() => setShowForm(true)} className="add-button">+ Nuevo Rol</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="modal">
          <form onSubmit={handleCreateRol} className="standard-form" style={{ width: '100%', maxWidth: '500px' }}>
            <h3>Crear Rol</h3>
            <div className="form-group">
              <label>Nombre del Rol</label>
              <input value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Descripcion</label>
              <input value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })} />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
              <button type="submit" className="add-button">Crear Rol</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' }}>
        <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', padding: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>Roles</h3>
          {loading ? <p>Cargando...</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {roles.map(rol => (
                <div
                  key={rol.id}
                  onClick={() => handleSelectRol(rol)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: selectedRol?.id === rol.id ? 'var(--primary)' : 'transparent',
                    color: selectedRol?.id === rol.id ? 'white' : 'var(--text-main)',
                    border: '1px solid var(--border-light)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{rol.nombre}</span>
                  {rol.es_sistema && <small style={{ opacity: 0.7 }}>Sistema</small>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {selectedRol ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '1.3rem' }}>{selectedRol.nombre}</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{selectedRol.descripcion || 'Sin descripcion'}</p>
                </div>
                {!selectedRol.es_sistema && (
                  <button onClick={() => handleDeleteRol(selectedRol.id)} className="btn-action btn-delete">Eliminar Rol</button>
                )}
              </div>

              {saving && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Guardando...</p>}

              <table className="standard-table">
                <thead>
                  <tr>
                    <th>Modulo</th>
                    <th>Ver</th>
                    <th>Crear</th>
                    <th>Editar</th>
                    <th>Eliminar</th>
                  </tr>
                </thead>
                <tbody>
                  {permisos.map(p => (
                    <tr key={p.id}>
                      <td>{p.modulo_nombre}</td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={p.puede_ver}
                          onChange={e => handleTogglePermiso(p.id, 'puede_ver', e.target.checked)}
                          disabled={selectedRol.es_sistema}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={p.puede_crear}
                          onChange={e => handleTogglePermiso(p.id, 'puede_crear', e.target.checked)}
                          disabled={selectedRol.es_sistema}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={p.puede_editar}
                          onChange={e => handleTogglePermiso(p.id, 'puede_editar', e.target.checked)}
                          disabled={selectedRol.es_sistema}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={p.puede_eliminar}
                          onChange={e => handleTogglePermiso(p.id, 'puede_eliminar', e.target.checked)}
                          disabled={selectedRol.es_sistema}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Selecciona un rol para ver y editar sus permisos.</p>
          )}
        </div>
      </div>
    </div>
  );
}
